import {
    Box,
    Button,
    TextField,
    Typography,
    Grid,
    FormControl,
    FormLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    FormGroup,
    Paper,
    Divider,
    Alert,
    CircularProgress,
    Chip,
} from "@mui/material";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { submitClinicianApplication } from "../api/services/user";
import { logo } from "../assets/index";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";

interface ApplicationState {
    // Personal Information
    fullName: string;
    idNumber: string;
    dateOfBirth: string;
    nationality: string;
    gender: string;
    mobileNumber: string;
    email: string;
    address: string;

    // Professional Information
    specialty: string[];
    otherSpecialty: string;
    professionalClassification: string;
    licenceNumber: string;
    issuingAuthority: string;
    licenceExpiryDate: string;
    yearsOfExperience: string;

    // Declaration
    declarationAccepted: boolean;
    signatureName: string;
    signatureDate: string;
}

const ClinicianApplication = () => {
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [uploadingFiles, setUploadingFiles] = useState(false);

    const [files, setFiles] = useState<{
        licenceDocument: File | null;
        idDocument: File | null;
        cvDocument: File | null;
    }>({
        licenceDocument: null,
        idDocument: null,
        cvDocument: null,
    });

    const [formData, setFormData] = useState<ApplicationState>({
        // Personal Information
        fullName: "",
        idNumber: "",
        dateOfBirth: "",
        nationality: "",
        gender: "",
        mobileNumber: "",
        email: "",
        address: "",

        // Professional Information
        specialty: [],
        otherSpecialty: "",
        professionalClassification: "",
        licenceNumber: "",
        issuingAuthority: "",
        licenceExpiryDate: "",
        yearsOfExperience: "",

        // Declaration
        declarationAccepted: false,
        signatureName: "",
        signatureDate: new Date().toISOString().split("T")[0],
    });

    const [errors, setErrors] = useState<any>({});

    const specialtyOptions = [
        "Psychologist",
        "Social Worker",
        "Speech and Language Therapist",
        "Occupational Therapist",
        "Physical Therapist",
        "Other",
    ];

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev: any) => ({ ...prev, [name]: "" }));
        }
    };

    const handleSpecialtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, checked } = e.target;
        setFormData((prev) => {
            const newSpecialty = checked
                ? [...prev.specialty, value]
                : prev.specialty.filter((s) => s !== value);
            return { ...prev, specialty: newSpecialty };
        });
        if (errors.specialty) {
            setErrors((prev: any) => ({ ...prev, specialty: "" }));
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fileType: 'licenceDocument' | 'idDocument' | 'cvDocument') => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setSubmitError(`File ${file.name} is too large. Maximum size is 5MB.`);
                return;
            }

            // Validate file type
            const allowedTypes = [
                "application/pdf",
                "image/jpeg",
                "image/jpg",
                "image/png",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ];

            if (!allowedTypes.includes(file.type)) {
                setSubmitError(
                    `Invalid file type for ${file.name}. Please upload PDF, JPG, PNG, or DOC files.`
                );
                return;
            }

            setFiles((prev) => ({
                ...prev,
                [fileType]: file,
            }));

            setSubmitError("");
        }
    };

    const handleRemoveFile = (fileType: 'licenceDocument' | 'idDocument' | 'cvDocument') => {
        setFiles((prev) => ({
            ...prev,
            [fileType]: null,
        }));
    };

    const validateForm = () => {
        const newErrors: any = {};

        // Personal Information Validation
        if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
        if (!formData.idNumber.trim())
            newErrors.idNumber = "ID/Residence number is required";
        if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
        if (!formData.nationality.trim())
            newErrors.nationality = "Nationality is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.mobileNumber.trim())
            newErrors.mobileNumber = "Mobile number is required";
        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Invalid email format";
        }
        if (!formData.address.trim()) newErrors.address = "Address is required";

        // Professional Information Validation
        if (formData.specialty.length === 0)
            newErrors.specialty = "Please select at least one specialty";
        if (
            formData.specialty.includes("Other") &&
            !formData.otherSpecialty.trim()
        ) {
            newErrors.otherSpecialty = "Please specify your specialty";
        }
        if (!formData.professionalClassification.trim())
            newErrors.professionalClassification =
                "Professional classification is required";
        if (!formData.licenceNumber.trim())
            newErrors.licenceNumber = "Licence number is required";
        if (!formData.issuingAuthority.trim())
            newErrors.issuingAuthority = "Issuing authority is required";
        if (!formData.licenceExpiryDate)
            newErrors.licenceExpiryDate = "Licence expiry date is required";
        if (!formData.yearsOfExperience)
            newErrors.yearsOfExperience = "Years of experience is required";

        // Declaration Validation
        if (!formData.declarationAccepted)
            newErrors.declarationAccepted =
                "You must accept the declaration to proceed";
        if (!formData.signatureName.trim())
            newErrors.signatureName = "Signature name is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const { mutate, isLoading } = useMutation(submitClinicianApplication, {
        onSuccess: (res: any) => {
            console.log("Application submitted successfully:", res);
            toast.success(
                "Thank you for your interest! We have received your registration and will contact you soon."
            );
            setSubmitSuccess(true);
            setSubmitError("");

            // Reset form
            setFormData({
                fullName: "",
                idNumber: "",
                dateOfBirth: "",
                nationality: "",
                gender: "",
                mobileNumber: "",
                email: "",
                address: "",
                specialty: [],
                otherSpecialty: "",
                professionalClassification: "",
                licenceNumber: "",
                issuingAuthority: "",
                licenceExpiryDate: "",
                yearsOfExperience: "",
                declarationAccepted: false,
                signatureName: "",
                signatureDate: new Date().toISOString().split("T")[0],
            });

            // Scroll to success message
            window.scrollTo({ top: 0, behavior: "smooth" });
        },
        onError: (err: any) => {
            console.error("Application submission error:", err.response);
            const errorMessage =
                err.response?.data?.message ||
                err.message ||
                "Failed to submit registration. Please try again later.";
            setSubmitError(errorMessage);
            toast.error(errorMessage);
        },
    });

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSubmitError("");

        if (!validateForm()) {
            setSubmitError("Please fill in all required fields correctly.");
            return;
        }

        // Prepare data for submission
        const submissionData = {
            full_name: formData.fullName,
            id_number: formData.idNumber,
            date_of_birth: formData.dateOfBirth,
            nationality: formData.nationality,
            gender: formData.gender.toUpperCase(),
            mobile_number: formData.mobileNumber,
            email: formData.email,
            address: formData.address,
            specialty: formData.specialty,
            other_specialty: formData.otherSpecialty || null,
            professional_classification: formData.professionalClassification,
            licence_number: formData.licenceNumber,
            issuing_authority: formData.issuingAuthority,
            licence_expiry_date: formData.licenceExpiryDate,
            years_of_experience: parseInt(formData.yearsOfExperience),
            declaration_accepted: formData.declarationAccepted,
            signature_name: formData.signatureName,
            signature_date: formData.signatureDate,
        };

        mutate(submissionData);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                py: { xs: 2, md: 3 },
                px: { xs: 2, md: 3 },
                background: "#f8f9fa",
            }}
        >
            <Box
                sx={{
                    maxWidth: "1100px",
                    margin: "0 auto",
                }}
            >
                {/* Form Section */}
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 3, md: 4 },
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    }}
                >
                    {/* Compact Header */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 3, pb: 2, borderBottom: "2px solid #e0e0e0" }}>
                        <img
                            src={logo}
                            alt="Talaqah Logo"
                            style={{ width: "60px", height: "60px", marginRight: "16px" }}
                        />
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 600,
                                    color: "#1a4d43",
                                    mb: 0.5,
                                }}
                            >
                                Clinician Application Form
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#666",
                                }}
                            >
                                Please complete all required fields
                            </Typography>
                        </Box>
                    </Box>

                    {submitSuccess && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            Thank you for your interest! We have received your registration
                            and will contact you soon.
                        </Alert>
                    )}

                    {submitError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {submitError}
                        </Alert>
                    )}

                    {uploadingFiles && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <CircularProgress size={20} />
                                <Typography>Uploading files...</Typography>
                            </Box>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        {/* Personal Information Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: "#1a4d43",
                                    mb: 2,
                                    pb: 1,
                                    borderBottom: "1px solid #e0e0e0",
                                }}
                            >
                                Personal Information
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Full Name *"
                                        name="fullName"
                                        value={formData.fullName}
                                        onChange={handleInputChange}
                                        error={!!errors.fullName}
                                        helperText={errors.fullName}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="ID / Residence Number *"
                                        name="idNumber"
                                        value={formData.idNumber}
                                        onChange={handleInputChange}
                                        error={!!errors.idNumber}
                                        helperText={errors.idNumber}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Date of Birth *"
                                        name="dateOfBirth"
                                        type="date"
                                        value={formData.dateOfBirth}
                                        onChange={handleInputChange}
                                        error={!!errors.dateOfBirth}
                                        helperText={errors.dateOfBirth}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Nationality *"
                                        name="nationality"
                                        value={formData.nationality}
                                        onChange={handleInputChange}
                                        error={!!errors.nationality}
                                        helperText={errors.nationality}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl
                                        component="fieldset"
                                        error={!!errors.gender}
                                        fullWidth
                                    >
                                        <FormLabel component="legend">Gender *</FormLabel>
                                        <RadioGroup
                                            row
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                        >
                                            <FormControlLabel
                                                value="male"
                                                control={<Radio />}
                                                label="Male"
                                            />
                                            <FormControlLabel
                                                value="female"
                                                control={<Radio />}
                                                label="Female"
                                            />
                                            <FormControlLabel
                                                value="other"
                                                control={<Radio />}
                                                label="Other"
                                            />
                                        </RadioGroup>
                                        {errors.gender && (
                                            <Typography variant="caption" color="error">
                                                {errors.gender}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Mobile Number *"
                                        name="mobileNumber"
                                        value={formData.mobileNumber}
                                        onChange={handleInputChange}
                                        error={!!errors.mobileNumber}
                                        helperText={errors.mobileNumber}
                                        variant="outlined"
                                        placeholder="+966XXXXXXXXX"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email Address *"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        error={!!errors.email}
                                        helperText={errors.email}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Address / City *"
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        error={!!errors.address}
                                        helperText={errors.address}
                                        variant="outlined"
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Professional Information Section */}
                        <Box sx={{ mb: 4 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: "#1a4d43",
                                    mb: 2,
                                    pb: 1,
                                    borderBottom: "1px solid #e0e0e0",
                                }}
                            >
                                Professional Information
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <FormControl
                                        component="fieldset"
                                        error={!!errors.specialty}
                                        fullWidth
                                    >
                                        <FormLabel component="legend">Specialty *</FormLabel>
                                        <FormGroup row>
                                            {specialtyOptions.map((option) => (
                                                <FormControlLabel
                                                    key={option}
                                                    control={
                                                        <Checkbox
                                                            value={option}
                                                            checked={formData.specialty.includes(option)}
                                                            onChange={handleSpecialtyChange}
                                                        />
                                                    }
                                                    label={option}
                                                    sx={{ minWidth: { xs: "100%", sm: "45%", md: "30%" } }}
                                                />
                                            ))}
                                        </FormGroup>
                                        {errors.specialty && (
                                            <Typography variant="caption" color="error">
                                                {errors.specialty}
                                            </Typography>
                                        )}
                                    </FormControl>
                                </Grid>

                                {formData.specialty.includes("Other") && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Please specify your specialty *"
                                            name="otherSpecialty"
                                            value={formData.otherSpecialty}
                                            onChange={handleInputChange}
                                            error={!!errors.otherSpecialty}
                                            helperText={errors.otherSpecialty}
                                            variant="outlined"
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Professional Classification *"
                                        name="professionalClassification"
                                        value={formData.professionalClassification}
                                        onChange={handleInputChange}
                                        error={!!errors.professionalClassification}
                                        helperText={errors.professionalClassification}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Licence Number *"
                                        name="licenceNumber"
                                        value={formData.licenceNumber}
                                        onChange={handleInputChange}
                                        error={!!errors.licenceNumber}
                                        helperText={errors.licenceNumber}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Issuing Authority *"
                                        name="issuingAuthority"
                                        value={formData.issuingAuthority}
                                        onChange={handleInputChange}
                                        error={!!errors.issuingAuthority}
                                        helperText={errors.issuingAuthority}
                                        variant="outlined"
                                        placeholder="e.g., Saudi Commission for Health Specialties"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Licence Expiry Date *"
                                        name="licenceExpiryDate"
                                        type="date"
                                        value={formData.licenceExpiryDate}
                                        onChange={handleInputChange}
                                        error={!!errors.licenceExpiryDate}
                                        helperText={errors.licenceExpiryDate}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Years of Experience *"
                                        name="yearsOfExperience"
                                        type="number"
                                        value={formData.yearsOfExperience}
                                        onChange={handleInputChange}
                                        error={!!errors.yearsOfExperience}
                                        helperText={errors.yearsOfExperience}
                                        variant="outlined"
                                        inputProps={{ min: 0 }}
                                    />
                                </Grid>

                                {/* File Upload Section */}
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="h6" sx={{ mb: 2, color: "#1a4d43" }}>
                                        Document Uploads (Recommended)
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                            Licence Document
                                        </Typography>
                                        {!files.licenceDocument ? (
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<CloudUploadIcon />}
                                                fullWidth
                                                sx={{
                                                    borderColor: "#3d7169",
                                                    color: "#3d7169",
                                                    "&:hover": {
                                                        borderColor: "#2a5850",
                                                        backgroundColor: "rgba(61, 113, 105, 0.05)",
                                                    },
                                                }}
                                            >
                                                Upload Licence Document
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                    onChange={(e) => handleFileChange(e, 'licenceDocument')}
                                                />
                                            </Button>
                                        ) : (
                                            <Chip
                                                icon={<AttachFileIcon />}
                                                label={files.licenceDocument.name}
                                                onDelete={() => handleRemoveFile('licenceDocument')}
                                                deleteIcon={<DeleteIcon />}
                                                sx={{
                                                    width: "100%",
                                                    justifyContent: "space-between",
                                                    height: "auto",
                                                    py: 1,
                                                    "& .MuiChip-label": {
                                                        display: "block",
                                                        whiteSpace: "normal",
                                                    },
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                            Accepted formats: PDF, JPG, PNG, DOC (Max 5MB)
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                            ID/Residence Document
                                        </Typography>
                                        {!files.idDocument ? (
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<CloudUploadIcon />}
                                                fullWidth
                                                sx={{
                                                    borderColor: "#3d7169",
                                                    color: "#3d7169",
                                                    "&:hover": {
                                                        borderColor: "#2a5850",
                                                        backgroundColor: "rgba(61, 113, 105, 0.05)",
                                                    },
                                                }}
                                            >
                                                Upload ID Document
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                    onChange={(e) => handleFileChange(e, 'idDocument')}
                                                />
                                            </Button>
                                        ) : (
                                            <Chip
                                                icon={<AttachFileIcon />}
                                                label={files.idDocument.name}
                                                onDelete={() => handleRemoveFile('idDocument')}
                                                deleteIcon={<DeleteIcon />}
                                                sx={{
                                                    width: "100%",
                                                    justifyContent: "space-between",
                                                    height: "auto",
                                                    py: 1,
                                                    "& .MuiChip-label": {
                                                        display: "block",
                                                        whiteSpace: "normal",
                                                    },
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                            Accepted formats: PDF, JPG, PNG, DOC (Max 5MB)
                                        </Typography>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                            CV/Resume
                                        </Typography>
                                        {!files.cvDocument ? (
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                startIcon={<CloudUploadIcon />}
                                                fullWidth
                                                sx={{
                                                    borderColor: "#3d7169",
                                                    color: "#3d7169",
                                                    "&:hover": {
                                                        borderColor: "#2a5850",
                                                        backgroundColor: "rgba(61, 113, 105, 0.05)",
                                                    },
                                                }}
                                            >
                                                Upload CV/Resume
                                                <input
                                                    type="file"
                                                    hidden
                                                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                    onChange={(e) => handleFileChange(e, 'cvDocument')}
                                                />
                                            </Button>
                                        ) : (
                                            <Chip
                                                icon={<AttachFileIcon />}
                                                label={files.cvDocument.name}
                                                onDelete={() => handleRemoveFile('cvDocument')}
                                                deleteIcon={<DeleteIcon />}
                                                sx={{
                                                    width: "100%",
                                                    justifyContent: "space-between",
                                                    height: "auto",
                                                    py: 1,
                                                    "& .MuiChip-label": {
                                                        display: "block",
                                                        whiteSpace: "normal",
                                                    },
                                                }}
                                            />
                                        )}
                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                                            Accepted formats: PDF, JPG, PNG, DOC (Max 5MB)
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        {/* Declaration Section */}
                        <Box sx={{ mb: 3 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: "#1a4d43",
                                    mb: 2,
                                    pb: 1,
                                    borderBottom: "1px solid #e0e0e0",
                                }}
                            >
                                Declaration
                            </Typography>

                            <Box
                                sx={{
                                    bgcolor: "#f9fafb",
                                    p: 2,
                                    borderRadius: 1,
                                    mb: 2,
                                    border: "1px solid #e5e7eb",
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="declarationAccepted"
                                            checked={formData.declarationAccepted}
                                            onChange={handleInputChange}
                                            color="primary"
                                        />
                                    }
                                    label="I declare that all information provided is accurate and complete *"
                                />
                                {errors.declarationAccepted && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        display="block"
                                        sx={{ mt: 1 }}
                                    >
                                        {errors.declarationAccepted}
                                    </Typography>
                                )}
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Signature (Full Name) *"
                                        name="signatureName"
                                        value={formData.signatureName}
                                        onChange={handleInputChange}
                                        error={!!errors.signatureName}
                                        helperText={errors.signatureName}
                                        variant="outlined"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Date *"
                                        name="signatureDate"
                                        type="date"
                                        value={formData.signatureDate}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Submit Buttons */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                justifyContent: "flex-end",
                                alignItems: "center",
                                flexDirection: { xs: "column", sm: "row" },
                                mt: 3,
                                pt: 3,
                                borderTop: "1px solid #e0e0e0",
                            }}
                        >
                            <Button
                                variant="text"
                                onClick={() => (window.location.href = "/login")}
                                disabled={isLoading || uploadingFiles}
                                sx={{
                                    textTransform: "none",
                                    color: "#666",
                                    "&:hover": {
                                        backgroundColor: "rgba(0,0,0,0.04)",
                                    },
                                }}
                            >
                                Already have an account?
                            </Button>

                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isLoading || uploadingFiles}
                                sx={{
                                    px: 4,
                                    py: 1.2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    bgcolor: "#3d7169",
                                    "&:hover": {
                                        bgcolor: "#2a5850",
                                    },
                                    minWidth: { xs: "100%", sm: "180px" },
                                }}
                            >
                                {isLoading || uploadingFiles ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    "Submit Registration"
                                )}
                            </Button>
                        </Box>
                    </form>
                </Paper>
            </Box>
        </Box>
    );
};

export default ClinicianApplication;